/**
 * 전송 전략 인터페이스 및 구현
 *
 * Strategy Pattern으로 Fetch/Beacon 전송 방식 추상화
 */

import type { LogEntry } from '../types';

import { toOtlpTraceRequest } from './otlp';

// ============================================
// Strategy Interface
// ============================================

export interface SendStrategy {
  send(
    entries: LogEntry[],
    endpoint: string,
    service: string,
    debug?: boolean,
  ): Promise<boolean>;
}

// ============================================
// Fetch Strategy
// ============================================

const FETCH_TIMEOUT_MS = 10_000; // 10초 타임아웃

/**
 * Fetch 기반 전송 전략
 * OTLP JSON 형식으로 /v1/traces에 전송
 */
export class FetchStrategy implements SendStrategy {
  async send(
    entries: LogEntry[],
    endpoint: string,
    service: string,
    debug = false,
  ): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const otlpPayload = toOtlpTraceRequest(entries, service);
      if (debug) {
        console.log(
          `[LoggerWorker] FetchStrategy sending ${entries.length} logs to ${endpoint}/v1/traces`,
        );
      }

      const response = await fetch(`${endpoint}/v1/traces`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(otlpPayload),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (debug) {
        console.log(
          `[LoggerWorker] FetchStrategy success: ${entries.length} logs sent`,
        );
      }
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error('[LoggerWorker] FetchStrategy failed:', errorMessage);
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

// ============================================
// Beacon Strategy
// ============================================

/**
 * Beacon 기반 전송 전략
 * 페이지 종료 시 사용 (fire-and-forget)
 * OTLP JSON 형식으로 /v1/traces에 전송
 *
 * 주의: Beacon 실패 시 fetch 폴백은 하지 않음
 * - 페이지 종료 중에는 fetch가 완료되기 전에 페이지가 닫힐 수 있음
 * - Beacon은 브라우저가 백그라운드에서 전송을 보장
 * - 다만, sendBeacon 미지원 환경에서는 fetch로 폴백
 */
export class BeaconStrategy implements SendStrategy {
  async send(
    entries: LogEntry[],
    endpoint: string,
    service: string,
    debug = false,
  ): Promise<boolean> {
    try {
      const hasBeacon =
        typeof navigator !== 'undefined' &&
        typeof navigator.sendBeacon === 'function';
      if (!hasBeacon) {
        if (debug) {
          console.warn(
            '[LoggerWorker] BeaconStrategy not supported in this environment',
          );
        }
        return new FetchStrategy().send(entries, endpoint, service, debug);
      }

      const otlpPayload = toOtlpTraceRequest(entries, service);
      const blob = new Blob([JSON.stringify(otlpPayload)], {
        type: 'application/json',
      });
      if (debug) {
        console.log(
          `[LoggerWorker] BeaconStrategy sending ${entries.length} logs`,
        );
      }
      const success = navigator.sendBeacon(`${endpoint}/v1/traces`, blob);

      if (!success) {
        console.warn(
          '[LoggerWorker] Beacon failed, data may be lost:',
          entries.length,
          'logs',
        );
      }

      return success;
    } catch (error) {
      console.error('[LoggerWorker] BeaconStrategy failed:', error);
      return false;
    }
  }
}
